import { ApiResponse } from "@/types/auth";
import { buildApiUrl, getAuthHeaders, API_CONFIG } from "@/config/api";

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  status: "UNREAD" | "READ";
  dealId?: string; 
  organizationId: string;
  userId: string;
  generatedForId: string;
  metadata?: {
    roleChangeRequestId?: string;
    status?: string;
    [key: string]: any;
  };
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationListResponse {
  success: boolean;
  data: Notification[];
}

class NotificationService {
  private baseUrl = buildApiUrl(API_CONFIG.ENDPOINTS.NOTIFICATIONS || "/notifications");
  private roleChangeBaseUrl = buildApiUrl("/role-changes");

  // Helper method to make API requests
  private async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    baseUrl: string = this.baseUrl
  ): Promise<ApiResponse<T>> {
    const response = await fetch(`${baseUrl}${endpoint}`, {
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || "Request failed");
    }

    return response.json();
  }

  // Get all notifications for the current user
  async getNotifications(): Promise<Notification[]> {
    const response = await this.apiRequest<NotificationListResponse>("");

    if (!response.success || !response.data) {
      throw new Error("Failed to get notifications");
    }

    return response.data;
  }

  // Mark a notification as read
  async markAsRead(notificationId: string): Promise<void> {
    const response = await this.apiRequest(`/${notificationId}/read`, {
      method: "PATCH",
    });

    if (!response.success) {
      throw new Error("Failed to mark notification as read");
    }
  }

  // Approve a role change request
  async approveRoleChangeRequest(requestId: string): Promise<void> {
    const response = await this.apiRequest(
      `/${requestId}/approve`,
      { method: "POST" },
      this.roleChangeBaseUrl
    );

    if (!response.success) {
      throw new Error("Failed to approve role change request");
    }
  }

  // Reject a role change request
  async rejectRoleChangeRequest(requestId: string): Promise<void> {
    const response = await this.apiRequest(
      `/${requestId}/reject`,
      { method: "POST" },
      this.roleChangeBaseUrl
    );

    if (!response.success) {
      throw new Error("Failed to reject role change request");
    }
  }

  // Get notification type icon
  getNotificationIcon(type: string): string {
    switch (type) {
      case "DEAL_CREATED":
        return "HandCoins";
      case "DEAL_UPDATED":
        return "Edit";
      case "DOCUMENT_UPLOADED":
        return "Upload";
      case "AI_REVIEW_COMPLETED":
        return "Bot";
      case "REPORT_GENERATED":
        return "FileBarChart";
      case "ROLE_CHANGE_REQUESTED":
        return "UserCog";
      case "ROLE_CHANGE_APPROVED":
      case "ROLE_CHANGE_REJECTED":
        return "UserCheck";
      default:
        return "Bell";
    }
  }

  // Get notification type color
  getNotificationTypeColor(type: string): string {
    switch (type) {
      case "DEAL_CREATED":
        return "text-blue-600 bg-blue-100";
      case "DEAL_UPDATED":
        return "text-green-600 bg-green-100";
      case "DOCUMENT_UPLOADED":
        return "text-purple-600 bg-purple-100";
      case "AI_REVIEW_COMPLETED":
        return "text-orange-600 bg-orange-100";
      case "REPORT_GENERATED":
        return "text-indigo-600 bg-indigo-100";
      case "ROLE_CHANGE_REQUESTED":
        return "text-teal-600 bg-teal-100";
      case "ROLE_CHANGE_APPROVED":
        return "text-green-600 bg-green-100";
      case "ROLE_CHANGE_REJECTED":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  }

  // Format notification date
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return "Today";
    } else if (diffDays === 2) {
      return "Yesterday";
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }

  // Format notification time
  formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Get time ago string
  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return "Just now";
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return this.formatDate(dateString);
    }
  }

  // Group notifications by date
  groupNotificationsByDate(notifications: Notification[]): Record<string, Notification[]> {
    const grouped: Record<string, Notification[]> = {};

    notifications.forEach((notification) => {
      const dateKey = this.formatDate(notification.createdAt);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(notification);
    });

    return grouped;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;